import React, { useMemo } from 'react'
import { NoteV2 } from '@labrav/react-components'
import { HelpDrawerWithAccordion } from '@labrav/common'
import { useStyles } from '../../../PrivateOfferFormV2/AzureOffer/MicrosoftFormFAQs/MicrosoftFormFAQs.styles'
import {
  FaqQuestion,
  HelpDrawerFaqItems,
} from '../../../AddEditProductFormComponents/FlyoutProductFormHelpFaqs/ProductCreationFaqUtil'

export enum CombinedFormFAQsItem {
  OFFER_DETAIL = 'offer-detail',
  PURCHASE_TERMS = 'purchase-terms',
  PAYMENT_CONFIGURATION = 'payment-configuration',
  PRICING = 'pricing',
  RENEWAL = 'renewal',
  EULA = 'eula',
  RESELLER_EULA = 'reseller-eula',
}

interface CombinedFormFAQsProps {
  item?: CombinedFormFAQsItem
}

export const CombinedFormFAQs: React.FC<CombinedFormFAQsProps> = ({ item }) => {
  const { classes } = useStyles()
  const faqItemsAuthorizationDetail: HelpDrawerFaqItems[] = [
    {
      question: (
        <FaqQuestion
          section={''}
          question="What should I enter as the Buyer name?"
        />
      ),
      answer: (
        <div>
          Enter the legal name of the buyer who will receive the offer. This
          should match the name associated with their Microsoft billing account.
        </div>
      ),
    },

    {
      question: (
        <FaqQuestion
          section={''}
          question="Where can I find the Buyer Billing Account ID?"
        />
      ),
      answer: (
        <div>
          This is the unique ID associated with the buyer's Microsoft billing
          account. The buyer can find this in their Microsoft Partner Center
          under their billing profile. Please verify with the buyer if unsure.
        </div>
      ),
    },

    {
      question: (
        <FaqQuestion
          section={''}
          question="What format should the Buyer Billing Account ID be in?"
        />
      ),
      answer: (
        <div>
          The Billing Account ID is a numeric string typically 9 to 12 digits
          long (e.g., 123456789). Ensure there are no extra spaces or
          characters. You can confirm this with the buyer's Microsoft admin.
        </div>
      ),
    },

    {
      question: (
        <FaqQuestion
          section={''}
          question="What should I enter as the Offer name?"
        />
      ),
      answer: (
        <div>
          <div>This is the name of the offer that will be visible to:</div>
          <ul className={classes.listItems}>
            <li>You (the offer creator)</li>
            <li> The ISV (Vendor) </li>
            <li>The end buyer when the offer is sent via Microsoft</li>
          </ul>
          <div className={classes.noteWrapper}>
            <NoteV2
              description={
                <div className={classes.note}>
                  Choose a name that is clear and easy to identify for all
                  parties involved.
                  <br />
                  Tip: Use a format like _BuyerName_Month_Year_DealType_ (e.g.,
                  Contoso_July2025_ResellerDiscount) for clarity.
                </div>
              }
            />
          </div>
        </div>
      ),
    },

    {
      question: (
        <FaqQuestion
          section={''}
          question="What happens after the Offer expiration date?"
        />
      ),
      answer: (
        <div>
          The buyer will no longer be able to accept the offer after this date.
          Make sure to set a realistic expiration timeline based on deal stage
          and internal approvals.
        </div>
      ),
    },

    {
      question: (
        <FaqQuestion
          section={''}
          question="Is the Offer description mandatory? What should I write here?"
        />
      ),
      answer: (
        <div>
          No, this field is optional. You can use it to briefly describe the
          offer’s purpose, special pricing notes, or any buyer-specific context.
          This is visible to the buyer.
        </div>
      ),
    },
  ]

  const paymentConfigurationFaqs = [
    {
      question: (
        <FaqQuestion
          section={''}
          question="What currency should I enter for pricing?"
        />
      ),
      answer: (
        <div>
          Currently only USD is enabled. Microsoft automatically converts the
          price entered in USD to local market currency using the monthly WMR
          exchange rate when you first save the price.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="How does currency conversion work in Microsoft Marketplace?"
        />
      ),
      answer: (
        <div>
          <div>
            When you enter a price in USD, it’s converted to each market’s local
            currency using the WM/Refinitiv monthly FX rate.
          </div>

          <ul className={classes.listItems}>
            <li>
              These converted local prices are locked when you save the plan and
              don’t change automatically with exchange rate fluctuations.
            </li>
            <li>
              They’ll only refresh if you update the price in the Partner
              Center.
            </li>
          </ul>
        </div>
      ),
    },

    {
      question: (
        <FaqQuestion
          section={''}
          question="When is foreign exchange (FX) applied?"
        />
      ),
      answer: (
        <div>
          <div>FX is applied at two possible points:</div>

          <ul className={classes.listItems}>
            <li>
              When the plan price is saved: USD is converted to each market
              currency using the monthly WMR rate.
            </li>
            <li>
              When Microsoft processes the transaction: If the buyer’s billing
              account and publisher’s payout currencies differ, another FX
              conversion applies based on the WMR rate for that transaction.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="What currency will you receive payouts in?"
        />
      ),
      answer: (
        <div>
          You’ll receive payouts in the payout currency configured in your
          Partner Center payout account.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="What happens if my payout currency is different from the buyer’s billing currency?"
        />
      ),
      answer: (
        <div>
          <div>
            Microsoft will convert the buyer’s payment amount from their billing
            currency to your payout currency using the monthly WMR exchange rate
            for that transaction month.
          </div>
          <div>
            This means the buyer always pays the equivalent of your set price in
            their local currency, but your payout may vary slightly depending on
            that month’s FX rate.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="How long does it take for updated prices to reflect if prices are updated from the Partner Center ?"
        />
      ),
      answer: (
        <div>
          <ul className={classes.listItems}>
            <li>Price increase: Takes effect after 90 days.</li>
            <li>Price decrease: Takes effect after 30 days.</li>
          </ul>
        </div>
      ),
    },
  ]

  const purchaseTermsFaqs = [
    {
      question: (
        <FaqQuestion
          section={''}
          question="What does “Purchase Start Month” mean?"
        />
      ),
      answer: (
        <div>
          <div>
            This determines when the buyer can begin purchasing under the
            private pricing
          </div>
          <ul className={classes.listItems}>
            <li>
              If you select{' '}
              <span className={classes.boldText}>Accepted Date</span>, the buyer
              can purchase as soon as they accept the offer.
            </li>
            <li>
              If you select{' '}
              <span className={classes.boldText}>Specific Month</span>, the
              buyer can purchase starting from the 1st day of that month (UTC).
              Purchases before this date will not be possible.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="What does “Purchase End Month” mean?"
        />
      ),
      answer: (
        <div>
          <div>
            This determines when the buyer can begin purchasing under the
            private pricing
          </div>
          <ul className={classes.listItems}>
            <li>
              If you select{' '}
              <span className={classes.boldText}>Accepted Date</span>, the buyer
              can purchase as soon as they accept the offer.
            </li>
            <li>
              If you select{' '}
              <span className={classes.boldText}>Specific Month</span>, the
              buyer can purchase starting from the 1st day of that month (UTC).
              Purchases before this date will not be possible.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="What if the buyer accepts the offer before offer expiry, but the purchase start date is in the future?"
        />
      ),
      answer: (
        <div>
          The buyer will not be able to make a purchase until the start month
          begins. The offer will remain in "accepted" state but unavailable for
          purchase until the pricing becomes active.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="Can I set a start month in the past?"
        />
      ),
      answer: (
        <div>
          <span className={classes.boldText}>No.</span> The start month must be
          the current or a future month.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="What happens if I select Accepted Date for a pay-as-you-go product?"
        />
      ),
      answer: (
        <div>
          The private pricing will apply for the{' '}
          <span className={classes.boldText}>entire calendar month</span> in
          which the offer is accepted even if it's accepted at the end of the
          month.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section={''} question="Can I change these dates later?" />
      ),
      answer: (
        <div>
          <span className={classes.boldText}>No.</span> Once the offer is
          published, start and end months cannot be edited. Please double-check
          before submitting.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="Does the start or end month impact billing cycles?"
        />
      ),
      answer: (
        <div>
          Microsoft billing and metering will follow the standard Marketplace
          behaviour. These fields only control when the private pricing becomes
          available and when it expires.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="Is there a minimum or maximum duration between start and end month?"
        />
      ),
      answer: (
        <div>
          Microsoft doesn’t impose a hard limit, but best practice is to ensure
          a reasonable window (e.g. 1–6 months) that gives your buyer time to
          complete the steps and purchase.
        </div>
      ),
    },
  ]

  const renewalFaqs = [
    {
      question: (
        <FaqQuestion
          section={''}
          question="What does “Renewal” mean in this context?"
        />
      ),
      answer: (
        <div>
          <div>
            This option is used to indicate whether the current offer is meant
            to renew an existing paid contract — either within Microsoft
            Commercial Marketplace or through an off-platform/private agreement.
          </div>
          <div>
            Selecting “Yes” flags this offer as a continuation or extension of
            an existing arrangement.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section={''} question="What happens if I select “Yes”?" />
      ),
      answer: (
        <div>
          The buyer and Microsoft will understand this is a renewal, not a
          brand-new deal. It may influence billing alignment, reporting, or
          buyer experience on Microsoft’s side.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section={''} question="When should I select “No”?" />
      ),
      answer: (
        <div>
          Choose No if:
          <ul className={classes.listItems}>
            <li>This is a net-new buyer or</li>
            <li>
              You're creating an entirely new contract, not tied to any existing
              paid agreement.
            </li>
          </ul>
        </div>
      ),
    },
  ]

  const pricingFaqs = [
    {
      question: (
        <FaqQuestion
          section={''}
          question="What is the purpose of the Pricing section?"
        />
      ),
      answer: (
        <div>
          The Pricing section allows you to define pricing details for the
          offer.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="What is a “Plan” and how do I select one?"
        />
      ),
      answer: (
        <div>
          <div>
            Plans are pre-defined offer structures created for your product
            (e.g., Basic, Pro, Enterprise).
          </div>
          <div>
            Select a plan from the dropdown — the description will auto-populate
            based on the selected plan.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="What is “Contract duration” inside a plan?"
        />
      ),
      answer: (
        <div>
          <div>
            This defines the billing term for the selected plan (e.g., 1 month,
            12 months).
          </div>
          <div>
            Each plan may support multiple durations. Choose the appropriate one
            you want to offer to the buyer.
          </div>
        </div>
      ),
    },

    {
      question: (
        <FaqQuestion
          section={''}
          question="What is the “Discounted TCV (to ISV)” field?"
        />
      ),
      answer: (
        <div>
          The Discounted TCV (to ISV) represents the total contract value that
          you offer to the ISV after applying a discount. This value indicates
          how much you agree to pay the ISV for the plans being offered to the
          buyer through the offer.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="What is the “Buyer’s marked-up price” field?"
        />
      ),
      answer: (
        <div>
          The Marked-up TCV (from Buyer) represents the total contract value
          that you charges the buyer after adding your markup to the ISV’s TCV.
          This amount reflects the final price the buyer will pay.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="How is the markup percentage calculated?"
        />
      ),
      answer: (
        <div>
          <div>
            The markup percentage is calculated using the following formula:
          </div>
          <div>
            Markup % = ((Marked-up TCV − Discounted TCV) / Discounted TCV) × 100
          </div>
          <div>
            It represents the margin added by you on top of the ISV’s discounted
            price.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="Why can’t I add an installment schedule?"
        />
      ),
      answer: (
        <div>
          You'll only see the installment schedule option when the contract
          duration is 12 months or more.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="How does the Installment Schedule work?"
        />
      ),
      answer: (
        <div>
          <div>The details of the working of installment schedule:</div>
          <ol className={classes.listItems}>
            <li>
              First invoice date is auto-set to contract start date (1st of the
              start month or the accepted date) and cannot be edited.
            </li>
            <li>
              You can choose how the remaining payments are spread — monthly,
              quarterly, yearly, or custom.
            </li>
            <li>
              Installment amounts must be:
              <ol type="a">
                <li>≥ 0</li>
                <li>≤ 100,000,000</li>
                <li>Up to 5 decimal places</li>
              </ol>
            </li>
            <li>You are allowed to add a maximum of 70 installments.</li>
            <li>
              Invoice date cannot be set after contract end date i.e. all
              installments must be taken during the contract period.
            </li>
          </ol>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section={''} question="What are Metering plans?" />
      ),
      answer: (
        <div>
          If your product is usage-based (e.g., data transfer, compute hours),
          you can define base quantities to offer the end buyer for predefined
          dimensions that are a part of the chosen plan. Enter the unit price
          for predefined plans in the ‘Price to ISV’ field — this is the price
          you’re offering to the ISV for that usage plan and the above markup %
          is automatically applied to calculate the buyer price.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section={''} question="What are Min-Max Users used for?" />
      ),
      answer: (
        <div>
          <div>If applicable, define user constraints:</div>
          <ul className={classes.listItems}>
            <li>Minimum number of users required to activate the offer</li>
            <li>Maximum number of users supported in the selected plan</li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section={''} question="Can I add multiple plans?" />
      ),
      answer: (
        <div>
          <div>Yes, you can add more plans using “Add Plan”.</div>
          <div>
            Each plan can have its own billing term, frequency, and pricing
            logic.
          </div>
        </div>
      ),
    },
  ]

  const eulaFaqs = [
    {
      question: (
        <FaqQuestion
          section={''}
          question="What is an EULA and why do I need one?"
        />
      ),
      answer: (
        <div>
          <div>
            EULA (End-user license agreement) defines the legal terms under
            which your product is offered to the buyer.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="What are available options for EULA configuration?"
        />
      ),
      answer: (
        <div>
          Currently only ISV’s Public offer legal terms supported for the end
          buyer.
        </div>
      ),
    },
  ]

  const resellerEulaFaqs = [
    {
      question: (
        <FaqQuestion
          section={''}
          question="What are available options for EULA configuration?"
        />
      ),
      answer: (
        <div>
          <div>
            You can upload Custom EULA- Upload your own EULA(s) in PDF format.
            You can upload up to 5 PDF files.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section={''}
          question="How many files can I upload for a Custom EULA ?"
        />
      ),
      answer: (
        <div>
          You can upload up to 5 PDF files, which may include multiple documents
          (e.g., license terms, privacy policy, addendums).
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section={''} question="Can I skip uploading a EULA ?" />
      ),
      answer: <div>Yes, this is optional for you to upload.</div>,
    },
  ]

  const faqItems = useMemo(() => {
    switch (item) {
      case CombinedFormFAQsItem.OFFER_DETAIL:
        return faqItemsAuthorizationDetail
      case CombinedFormFAQsItem.PURCHASE_TERMS:
        return purchaseTermsFaqs
      case CombinedFormFAQsItem.PAYMENT_CONFIGURATION:
        return paymentConfigurationFaqs
      case CombinedFormFAQsItem.PRICING:
        return pricingFaqs
      case CombinedFormFAQsItem.RENEWAL:
        return renewalFaqs
      case CombinedFormFAQsItem.EULA:
        return eulaFaqs
      case CombinedFormFAQsItem.RESELLER_EULA:
        return resellerEulaFaqs
      default:
        return []
    }
  }, [item])

  return (
    <HelpDrawerWithAccordion
      headerTitle="Help & FAQs"
      faqItems={faqItems}
      divider
    />
  )
}
